import type {
  IStoreCartDesignGroupApiStatus,
  StoreCartDesignGroupApiStatus,
} from '../storeCart.constants';
import { DEFAULT_STORE_CART_DESIGN_GROUP_API_STATUS } from '../storeCart.constants';
import { getStoreCartDesignGroupKey } from './storeCartDesignGroups.utils';
import type { ICartItemDetail } from '@/common/interfaces/cart.interface';

export const getStoreCartDesignGroupApiStatus = (
  statusByKey: Record<string, IStoreCartDesignGroupApiStatus>,
  groupKey: string
): IStoreCartDesignGroupApiStatus =>
  statusByKey[groupKey] ?? DEFAULT_STORE_CART_DESIGN_GROUP_API_STATUS;

export const isStoreCartDesignGroupApiReady = (
  status: IStoreCartDesignGroupApiStatus | undefined
): boolean => status?.quote === 'success' && status?.moq === 'success';

export const isStoreCartDesignGroupApiPending = (
  status: IStoreCartDesignGroupApiStatus | undefined
): boolean =>
  !status ||
  status.quote === 'idle' ||
  status.quote === 'loading' ||
  status.moq === 'idle' ||
  status.moq === 'loading';

export const shouldShowStoreCartDesignGroupPrice = (
  status: IStoreCartDesignGroupApiStatus | undefined
): boolean => status?.quote === 'success';

export const shouldShowStoreCartDesignGroupMoqTiers = (
  status: IStoreCartDesignGroupApiStatus | undefined
): boolean => status?.moq === 'success';

export const isStoreCartDesignGroupQuotePending = (
  status: IStoreCartDesignGroupApiStatus | undefined
): boolean => !status || status.quote === 'idle' || status.quote === 'loading';

export const isStoreCartDesignGroupMoqPending = (
  status: IStoreCartDesignGroupApiStatus | undefined
): boolean => !status || status.moq === 'idle' || status.moq === 'loading';

export const hasStoreCartDesignGroupQuoteFailed = (
  status: IStoreCartDesignGroupApiStatus | undefined
): boolean => status?.quote === 'error';

export const hasStoreCartDesignGroupMoqFailed = (
  status: IStoreCartDesignGroupApiStatus | undefined
): boolean => status?.moq === 'error';

/** True when an item cannot self-checkout (block_self_checkout or licensing rejection). */
export const isStoreCartItemBlockedForCheckout = (item: ICartItemDetail): boolean =>
  Boolean(
    item.designRejectionInfo?.isDesignSelfBlockedForCheckout ||
      item.designRejectionInfo?.isDesignLicensingRejected
  );

export const hasStoreCartBlockedCheckoutItems = (cartItems: ICartItemDetail[]): boolean =>
  cartItems.some(isStoreCartItemBlockedForCheckout);

export const isStoreCartSetupStoreDisabled = (
  filteredCartItems: ICartItemDetail[],
  designGroupApiStatusByKey: Record<string, IStoreCartDesignGroupApiStatus>
): boolean => {
  if (!filteredCartItems.length) {
    return true;
  }

  if (hasStoreCartBlockedCheckoutItems(filteredCartItems)) {
    return true;
  }

  const designGroupKeys = new Set(
    filteredCartItems.map((item) => getStoreCartDesignGroupKey(item))
  );

  return Array.from(designGroupKeys).some((groupKey) => {
    const status = getStoreCartDesignGroupApiStatus(designGroupApiStatusByKey, groupKey);

    return !isStoreCartDesignGroupApiReady(status);
  });
};

export const buildStoreCartDesignGroupMoqStatuses = (
  cartItems: ICartItemDetail[],
  moqsByGroupKey: Record<string, number>
): Record<string, StoreCartDesignGroupApiStatus> => {
  const statuses: Record<string, StoreCartDesignGroupApiStatus> = {};

  cartItems.forEach((item) => {
    const groupKey = getStoreCartDesignGroupKey(item);
    const moq = moqsByGroupKey[groupKey];

    // Missing, 0, or positive maxMoq → success. Callers fall back to default breakpoints
    // when maxMoq is unavailable or 0; never block Select Minimum on MOQ API failure.
    statuses[groupKey] = moq == null || (Number.isFinite(moq) && moq >= 0) ? 'success' : 'error';
  });

  return statuses;
};
